(() => {
  if (window.ReBoxPriceFilterLoaded) return;
  window.ReBoxPriceFilterLoaded = true;

  const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max);
  };

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const toSubmissionValue = (value) => {
    const rounded = Math.round(value * 100) / 100;

    return Number.isInteger(rounded)
      ? String(rounded)
      : rounded.toFixed(2);
  };

  const initialisePriceFilter = (widget) => {
    if (widget.dataset.rbReady === 'true') return;

    const rangeMin = widget.querySelector('[data-rb-range-min]');
    const rangeMax = widget.querySelector('[data-rb-range-max]');
    const inputMin = widget.querySelector('[data-rb-input-min]');
    const inputMax = widget.querySelector('[data-rb-input-max]');
    const submitMin = widget.querySelector('[data-rb-submit-min]');
    const submitMax = widget.querySelector('[data-rb-submit-max]');
    const slider = widget.querySelector('[data-rb-slider]');
    const histogram = widget.querySelector('[data-rb-histogram]');
    const histogramRange = widget.querySelector(
      '[data-rb-histogram-range]'
    );
    const bars = Array.from(
      widget.querySelectorAll('[data-rb-bar]')
    );
    const selection = widget.querySelector('[data-rb-selection]');

    if (
      !rangeMin ||
      !rangeMax ||
      !inputMin ||
      !inputMax ||
      !submitMin ||
      !submitMax ||
      !slider
    ) {
      return;
    }

    widget.dataset.rbReady = 'true';

    const absoluteMin = toNumber(rangeMin.min, 0);
    const absoluteMax = toNumber(rangeMax.max, 0);
    const currency = widget.dataset.currency || 'EUR';
    const locale = widget.dataset.locale || 'it-IT';
    const epsilon = 0.005;

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const setActiveHandle = (handle) => {
      const minIsActive = handle === 'min';

      rangeMin.classList.toggle('is-active', minIsActive);
      rangeMax.classList.toggle('is-active', !minIsActive);

      rangeMin.style.zIndex = minIsActive ? '5' : '3';
      rangeMax.style.zIndex = minIsActive ? '3' : '5';
    };

    const getValues = () => {
      let minValue = clamp(
        toNumber(rangeMin.value, absoluteMin),
        absoluteMin,
        absoluteMax
      );

      let maxValue = clamp(
        toNumber(rangeMax.value, absoluteMax),
        absoluteMin,
        absoluteMax
      );

      if (minValue > maxValue) {
        minValue = maxValue;
        rangeMin.value = String(minValue);
      }

      return {
        minValue,
        maxValue,
      };
    };

    const syncSubmissionInputs = (minValue, maxValue) => {
      submitMin.value =
        minValue > absoluteMin + epsilon
          ? toSubmissionValue(minValue)
          : '';

      submitMax.value =
        maxValue < absoluteMax - epsilon
          ? toSubmissionValue(maxValue)
          : '';
    };

    const updateHistogram = (minPercent, maxPercent) => {
      if (histogram) {
        histogram.style.setProperty(
          '--rb-range-start',
          `${minPercent}%`
        );

        histogram.style.setProperty(
          '--rb-range-end',
          `${maxPercent}%`
        );
      }

      if (histogramRange) {
        histogramRange.style.left = `${minPercent}%`;
        histogramRange.style.right = `${100 - maxPercent}%`;
      }

      bars.forEach((bar, index) => {
        const position =
          bars.length > 1
            ? (index / (bars.length - 1)) * 100
            : 0;

        const isInRange =
          position >= minPercent &&
          position <= maxPercent;

        bar.classList.toggle('is-in-range', isInRange);
      });
    };

    const updateAppearance = ({
      syncVisibleInputs = true,
    } = {}) => {
      const { minValue, maxValue } = getValues();

      const total = absoluteMax - absoluteMin || 1;

      const minPercent =
        ((minValue - absoluteMin) / total) * 100;

      const maxPercent =
        ((maxValue - absoluteMin) / total) * 100;

      slider.style.setProperty(
        '--rb-range-start',
        `${minPercent}%`
      );

      slider.style.setProperty(
        '--rb-range-end',
        `${maxPercent}%`
      );

      updateHistogram(minPercent, maxPercent);
      syncSubmissionInputs(minValue, maxValue);

      if (syncVisibleInputs) {
        inputMin.value = toSubmissionValue(minValue);
        inputMax.value = toSubmissionValue(maxValue);
      }

      if (selection) {
        selection.textContent =
          `Intervallo selezionato: ${formatter.format(minValue)} – ${formatter.format(maxValue)}`;
      }
    };

    const updateFromMinRange = (event) => {
      event.stopPropagation();
      setActiveHandle('min');

      if (
        toNumber(rangeMin.value) >
        toNumber(rangeMax.value)
      ) {
        rangeMin.value = rangeMax.value;
      }

      updateAppearance();
    };

    const updateFromMaxRange = (event) => {
      event.stopPropagation();
      setActiveHandle('max');

      if (
        toNumber(rangeMax.value) <
        toNumber(rangeMin.value)
      ) {
        rangeMax.value = rangeMin.value;
      }

      updateAppearance();
    };

    const updateFromMinInput = (event) => {
      event.stopPropagation();

      if (inputMin.value === '') return;

      const value = clamp(
        toNumber(inputMin.value, absoluteMin),
        absoluteMin,
        toNumber(rangeMax.value, absoluteMax)
      );

      rangeMin.value = String(value);

      setActiveHandle('min');
      updateAppearance();
    };

    const updateFromMaxInput = (event) => {
      event.stopPropagation();

      if (inputMax.value === '') return;

      const value = clamp(
        toNumber(inputMax.value, absoluteMax),
        toNumber(rangeMin.value, absoluteMin),
        absoluteMax
      );

      rangeMax.value = String(value);

      setActiveHandle('max');
      updateAppearance();
    };

    rangeMin.addEventListener('pointerdown', () => {
      setActiveHandle('min');
    });

    rangeMax.addEventListener('pointerdown', () => {
      setActiveHandle('max');
    });

    rangeMin.addEventListener(
      'input',
      updateFromMinRange
    );

    rangeMax.addEventListener(
      'input',
      updateFromMaxRange
    );

    inputMin.addEventListener(
      'input',
      updateFromMinInput
    );

    inputMax.addEventListener(
      'input',
      updateFromMaxInput
    );

    rangeMin.addEventListener(
      'change',
      (event) => {
        event.stopImmediatePropagation();
        updateFromMinRange(event);
      },
      true
    );

    rangeMax.addEventListener(
      'change',
      (event) => {
        event.stopImmediatePropagation();
        updateFromMaxRange(event);
      },
      true
    );

    inputMin.addEventListener(
      'change',
      (event) => {
        event.stopImmediatePropagation();

        if (inputMin.value === '') {
          rangeMin.value = String(absoluteMin);
        }

        updateFromMinInput(event);
        updateAppearance();
      },
      true
    );

    inputMax.addEventListener(
      'change',
      (event) => {
        event.stopImmediatePropagation();

        if (inputMax.value === '') {
          rangeMax.value = String(absoluteMax);
        }

        updateFromMaxInput(event);
        updateAppearance();
      },
      true
    );

    setActiveHandle('max');
    updateAppearance();
  };

  const initialiseAll = (root = document) => {
    root
      .querySelectorAll('[data-rb-price-filter]')
      .forEach(initialisePriceFilter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseAll()
    );
  } else {
    initialiseAll();
  }

  const observer = new MutationObserver(() => {
    initialiseAll();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();